import type React from 'react';
import { useEffect } from 'react';
import styled from 'styled-components';
import { useCommunicationService } from 'src/app/services/communication';
import { MessageType } from 'src/app/utils/in-app-comunication/types';
import { getActiveSection } from 'src/app/ui/components/molecules/whiteboard-slide/util';

type TimerProps = {
  className?: string;
  isPresenterView?: boolean;
  elapsedTime: number;
  reset: () => void;
  timerEnabled?: boolean;
  timeSections?: { workTime: number; restTime: number }[];
};

const TimerWrapper = styled.div<{ $isPresenterView?: boolean; timerEnabled: boolean; isWorkTime?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  border: ${({ theme, $isPresenterView, isWorkTime }) => ($isPresenterView ? 'none' : `2px solid ${isWorkTime ? theme.colors.green : theme.colors.firebreathingRed}`)};
  background: ${({ theme, $isPresenterView }) =>
    $isPresenterView ? 'transparent' : theme.colors.greenWithTransparency};
  border-radius: ${({ theme }) => theme.spacing(3)};
  color: ${({ theme, timerEnabled }) => (timerEnabled ? theme.colors.white : theme.colors.lightGray)};
  font-weight: 600;
  font-size: ${({ theme }) => theme.typography.jumbo};
  width: auto;
`;

const Digit = styled.span<{ timerEnabled: boolean }>`
  display: inline-block;
  width: 0.97ch;
  text-align: center;
  color: ${({ theme, timerEnabled }) => (timerEnabled ? theme.colors.white : theme.colors.lightGray)};
`;

const Separator = styled.div<{ timerEnabled: boolean }>`
  margin: 0 ${({ theme }) => theme.spacing(1)};
  color: ${({ theme, timerEnabled }) => (timerEnabled ? theme.colors.white : theme.colors.lightGray)};
`;

const Timer: React.FC<TimerProps> = ({
  className,
  isPresenterView = false,
  elapsedTime,
  reset,
  timerEnabled = true,
  timeSections,
}) => {
  const comms = useCommunicationService();
  useEffect(() => {
    const unsubscribe = comms.onMessage((message) => {
      if (message.type === MessageType.SLIDESHOW_CHANGE) {
        reset();
      }
    });
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [comms, reset]);

  const isWorkTime = getActiveSection(elapsedTime, timeSections?.[0]) === 'Work';
  const safeElapsedTime = typeof elapsedTime === 'number' && !Number.isNaN(elapsedTime) ? elapsedTime : 0;
  const minutes = String(Math.floor(safeElapsedTime / 60)).padStart(2, '0');
  const seconds = String(Math.floor(safeElapsedTime % 60)).padStart(2, '0');

  return (
    <TimerWrapper
      data-testid="timer-wrapper"
      className={className}
      $isPresenterView={isPresenterView}
      timerEnabled={timerEnabled}
      isWorkTime={isWorkTime}
    >
      <Digit timerEnabled={timerEnabled}>{minutes[0]}</Digit>
      <Digit timerEnabled={timerEnabled}>{minutes[1]}</Digit>
      <Separator timerEnabled={timerEnabled}>:</Separator>
      <Digit timerEnabled={timerEnabled}>{seconds[0]}</Digit>
      <Digit timerEnabled={timerEnabled}>{seconds[1]}</Digit>
    </TimerWrapper>
  );
};

export default Timer;
